import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { meetingsAPI, gcalAPI } from '../lib/api';
import {
  Calendar as CalendarIcon, Clock, Video, Users, FileText, Plus, Pencil,
  Trash2, ChevronLeft, ChevronRight, RefreshCw, CalendarClock, Star,
  Clapperboard, CheckCircle2,
} from 'lucide-react';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const emptyForm = () => ({
  titulo: '', tipo: 'general', fecha: '', horaInicio: '', horaFin: '',
  participantes: '', meet_link: '', notas: '',
  grabFecha: '', grabInicio: '', grabFin: '',
});

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function ClientMeetings({ clientId, clientName }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [gcal, setGcal] = useState({ connected: false, configured: false, email: null });
  const [syncing, setSyncing] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [invitees, setInvitees] = useState([]);
  const [extraEmail, setExtraEmail] = useState('');

  const loadGcal = useCallback(async () => {
    try { setGcal(await gcalAPI.status()); } catch { /* noop */ }
  }, []);
  useEffect(() => { loadGcal(); }, [loadGcal]);

  // Handle OAuth return (?gcal=connected|error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get('gcal');
    if (g === 'connected') {
      toast.success('Google Calendar conectado');
      loadGcal();
      params.delete('gcal');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params}` : ''));
    } else if (g === 'error') {
      toast.error('No se pudo conectar Google Calendar');
      params.delete('gcal');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params}` : ''));
    }
  }, [loadGcal]);

  const connectGcal = async () => {
    try {
      const { authorization_url } = await gcalAPI.login();
      window.location.href = authorization_url;
    } catch (e) { toast.error(e.message || 'No se pudo iniciar la conexión'); }
  };

  const syncGcal = async () => {
    setSyncing(true);
    try {
      const res = await meetingsAPI.syncGoogle();
      toast.success(`Sincronizado (${res.created || 0} añadidas, ${res.updated} actualizadas, ${res.removed} eliminadas)`);
      await load();
    } catch (e) { toast.error(e.message || 'Error al sincronizar'); }
    finally { setSyncing(false); }
  };

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await meetingsAPI.listForClient(clientId);
      setMeetings(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('No se pudieron cargar las reuniones');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    setForm(emptyForm()); setEditingId(null); setExtraEmail(''); setDialogOpen(true);
    try {
      const cs = await meetingsAPI.getContacts(clientId);
      const list = Array.isArray(cs) ? cs : [];
      setContacts(list);
      setInvitees(list.filter((c) => c.primary).map((c) => c.email));
    } catch { setContacts([]); setInvitees([]); }
  };

  const openEdit = (m) => {
    const start = m.start_datetime || '';
    const end = m.end_datetime || '';
    setForm({
      titulo: m.titulo || '',
      tipo: m.tipo || 'general',
      fecha: start.slice(0, 10),
      horaInicio: start.slice(11, 16),
      horaFin: end.slice(11, 16),
      participantes: (m.participantes || []).join(', '),
      meet_link: m.meet_link || '',
      notas: m.notas || '',
    });
    setEditingId(m.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.titulo.trim()) { toast.error('Añade un título'); return; }
    if (!form.fecha || !form.horaInicio) { toast.error('Añade fecha y hora de inicio'); return; }
    setSaving(true);
    try {
      const payload = {
        titulo: form.titulo.trim(),
        tipo: form.tipo,
        start_datetime: `${form.fecha}T${form.horaInicio}`,
        end_datetime: form.horaFin ? `${form.fecha}T${form.horaFin}` : null,
        participantes: form.participantes.split(',').map((s) => s.trim()).filter(Boolean),
        meet_link: form.meet_link.trim() || null,
        notas: form.notas.trim() || null,
      };
      if (!editingId && form.grabFecha && form.grabInicio) {
        payload.recording_start_datetime = `${form.grabFecha}T${form.grabInicio}`;
        payload.recording_end_datetime = form.grabFin ? `${form.grabFecha}T${form.grabFin}` : null;
      }
      if (!editingId) {
        payload.invitados = invitees;
      }
      if (editingId) {
        await meetingsAPI.update(editingId, payload);
        toast.success('Reunión actualizada');
      } else {
        await meetingsAPI.create(clientId, payload);
        toast.success('Reunión creada');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`¿Eliminar la reunión "${m.titulo}"?`)) return;
    try {
      await meetingsAPI.delete(m.id);
      toast.success('Reunión eliminada');
      await load();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  const now = new Date();
  const upcoming = useMemo(
    () => meetings.filter((m) => new Date(m.start_datetime) >= now).sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)),
    [meetings]
  );
  const past = useMemo(
    () => meetings.filter((m) => new Date(m.start_datetime) < now).sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime)),
    [meetings]
  );

  // ----- mini calendar -----
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const meetingsByDay = useMemo(() => {
    const map = {};
    for (const m of meetings) {
      const d = new Date(m.start_datetime);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        (map[day] = map[day] || []).push(m);
      }
    }
    return map;
  }, [meetings, year, month]);

  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const MeetingCard = ({ m, isPast }) => (
    <div
      className={`rounded-lg border p-3 ${isPast ? 'bg-muted/30 opacity-80' : 'bg-card'} transition-colors hover:border-primary/40`}
      data-testid={`meeting-card-${m.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium truncate">{m.titulo}</p>
            {m.tipo === 'planificacion' && (
              <Badge className="bg-amber-100 text-amber-800 gap-1 text-[10px]"><Star className="w-3 h-3" /> Planificación</Badge>
            )}
            {m.tipo === 'grabacion' && (
              <Badge className="bg-purple-100 text-purple-800 gap-1 text-[10px]"><Clapperboard className="w-3 h-3" /> Grabación</Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDateTime(m.start_datetime)}</span>
            {m.participantes?.length > 0 && (
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {m.participantes.join(', ')}</span>
            )}
            {m.invitados?.length > 0 && (
              <span className="flex items-center gap-1" data-testid={`meeting-invitados-${m.id}`}><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Invitado: {m.invitados.join(', ')}</span>
            )}
          </div>
          {m.meet_link && (
            <a
              href={m.meet_link} target="_blank" rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              data-testid={`meeting-link-${m.id}`}
            >
              <Video className="w-3 h-3" /> Unirse a la videollamada
            </a>
          )}
          {m.notas && (
            <p className="mt-2 flex items-start gap-1 text-xs text-muted-foreground">
              <FileText className="w-3 h-3 mt-0.5 shrink-0" /> <span className="whitespace-pre-wrap">{m.notas}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(m)} data-testid={`meeting-edit-${m.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(m)} data-testid={`meeting-delete-${m.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="client-meetings-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><CalendarClock className="w-5 h-5 text-primary" /> Reuniones</h3>
          <p className="text-sm text-muted-foreground">Reuniones con {clientName || 'el cliente'}: planificación, seguimiento y actas.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {gcal.configured && (gcal.connected ? (
            <>
              <Badge className="bg-emerald-100 text-emerald-800 gap-1" data-testid="gcal-connected-badge">
                <CheckCircle2 className="w-3 h-3" /> Google Calendar
              </Badge>
              <Button variant="outline" size="sm" onClick={syncGcal} disabled={syncing} data-testid="gcal-sync-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sincronizar
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={connectGcal} data-testid="gcal-connect-btn">
              <CalendarIcon className="w-4 h-4 mr-1" /> Conectar Google Calendar
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={load} data-testid="meetings-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          <Button size="sm" onClick={openCreate} data-testid="meetings-new-btn"><Plus className="w-4 h-4 mr-1" /> Nueva reunión</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mini calendar */}
        <Card className="lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewDate(new Date(year, month - 1, 1))} data-testid="cal-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm font-medium">{MONTHS[month]} {year}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewDate(new Date(year, month + 1, 1))} data-testid="cal-next-btn"><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {WEEKDAYS.map((w) => <div key={w} className="text-[10px] font-medium text-muted-foreground py-1">{w}</div>)}
              {cells.map((d, i) => {
                if (d === null) return <div key={`e${i}`} />;
                const dayMeetings = meetingsByDay[d] || [];
                const isToday = now.getFullYear() === year && now.getMonth() === month && now.getDate() === d;
                const hasPlan = dayMeetings.some((m) => m.tipo === 'planificacion');
                const hasRec = dayMeetings.some((m) => m.tipo === 'grabacion');
                const dotColor = hasPlan ? 'bg-amber-500' : hasRec ? 'bg-purple-500' : 'bg-primary';
                return (
                  <button
                    key={d}
                    onClick={() => dayMeetings.length && openEdit(dayMeetings[0])}
                    className={`relative aspect-square rounded-md text-xs flex items-center justify-center ${isToday ? 'ring-1 ring-primary' : ''} ${dayMeetings.length ? 'bg-primary/10 font-medium cursor-pointer hover:bg-primary/20' : 'text-muted-foreground'}`}
                    data-testid={`cal-day-${d}`}
                  >
                    {d}
                    {dayMeetings.length > 0 && (
                      <span className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${dotColor}`} />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> Reunión</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Planificación</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-purple-500" /> Grabación</span>
            </div>
          </CardContent>
        </Card>

        {/* Lists */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Próximas ({upcoming.length})</h4>
            {loading ? (
              <p className="text-sm text-muted-foreground">Cargando…</p>
            ) : upcoming.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="meetings-empty-upcoming">
                No hay reuniones próximas. Crea la primera con "Nueva reunión".
              </div>
            ) : (
              <div className="space-y-2">{upcoming.map((m) => <MeetingCard key={m.id} m={m} />)}</div>
            )}
          </div>
          {past.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Pasadas ({past.length})</h4>
              <div className="space-y-2">{past.map((m) => <MeetingCard key={m.id} m={m} isPast />)}</div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="meeting-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar reunión' : 'Nueva reunión'}</DialogTitle>
            <DialogDescription>Completa los datos de la reunión con {clientName || 'el cliente'}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ej. Reunión de planificación de contenido" data-testid="meeting-titulo-input" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="meeting-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Reunión general</SelectItem>
                  <SelectItem value="planificacion">Planificación mensual de contenido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="meeting-fecha-input" />
              </div>
              <div>
                <Label>Inicio</Label>
                <Input type="time" value={form.horaInicio} onChange={(e) => setForm({ ...form, horaInicio: e.target.value })} data-testid="meeting-inicio-input" />
              </div>
              <div>
                <Label>Fin</Label>
                <Input type="time" value={form.horaFin} onChange={(e) => setForm({ ...form, horaFin: e.target.value })} data-testid="meeting-fin-input" />
              </div>
            </div>
            <div>
              <Label>Participantes <span className="text-xs text-muted-foreground">(separados por comas)</span></Label>
              <Input value={form.participantes} onChange={(e) => setForm({ ...form, participantes: e.target.value })} placeholder="Karmele, Marta, Cliente…" data-testid="meeting-participantes-input" />
            </div>
            {!editingId && (
              <div className="rounded-lg border p-3 space-y-2" data-testid="invite-client-section">
                <div className="flex items-center gap-2 text-sm font-medium"><Users className="w-4 h-4 text-primary" /> Invitar al cliente</div>
                {contacts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No hay ningún email en la ficha del cliente. Añade uno abajo para invitarle.</p>
                ) : (
                  <div className="space-y-1">
                    {contacts.map((c) => (
                      <label key={c.email} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`invite-contact-${c.email}`}>
                        <input
                          type="checkbox"
                          className="accent-primary"
                          checked={invitees.includes(c.email)}
                          onChange={(e) => setInvitees((prev) => e.target.checked ? [...new Set([...prev, c.email])] : prev.filter((x) => x !== c.email))}
                        />
                        <span>{c.email}{c.name ? ` · ${c.name}` : ''}{c.primary ? ' (principal)' : ''}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input value={extraEmail} onChange={(e) => setExtraEmail(e.target.value)} placeholder="Añadir otro email…" data-testid="invite-extra-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    const em = extraEmail.trim().toLowerCase();
                    if (em && em.includes('@')) {
                      setInvitees((prev) => [...new Set([...prev, em])]);
                      setContacts((prev) => prev.some((c) => c.email === em) ? prev : [...prev, { email: em, name: '', primary: false }]);
                      setExtraEmail('');
                    } else { toast.error('Email no válido'); }
                  }} data-testid="invite-add-btn">Añadir</Button>
                </div>
                <p className="text-xs text-muted-foreground">Los seleccionados recibirán la invitación de Google Calendar con el enlace de Meet.</p>
              </div>
            )}
            <div>
              <Label>Enlace de videollamada <span className="text-xs text-muted-foreground">(Meet/Zoom)</span></Label>
              <Input value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} placeholder="https://meet.google.com/…" data-testid="meeting-link-input" />
              {gcal.connected && !editingId && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Video className="w-3 h-3" /> Se generará un enlace de Google Meet automáticamente al crear la reunión.</p>
              )}
            </div>
            <div>
              <Label>Notas / Acta</Label>
              <Textarea rows={4} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Puntos a tratar, acuerdos, próximos pasos…" data-testid="meeting-notas-input" />
            </div>
            {!editingId && (
              <div className="rounded-lg border border-dashed p-3 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium"><Clapperboard className="w-4 h-4 text-purple-600" /> Día de grabación <span className="text-xs text-muted-foreground font-normal">(opcional)</span></div>
                <p className="text-xs text-muted-foreground">Cierra a la vez el día de grabación. Se crea como una reunión aparte y también aparece en el cliente.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Fecha</Label>
                    <Input type="date" value={form.grabFecha} onChange={(e) => setForm({ ...form, grabFecha: e.target.value })} data-testid="meeting-grab-fecha-input" />
                  </div>
                  <div>
                    <Label>Inicio</Label>
                    <Input type="time" value={form.grabInicio} onChange={(e) => setForm({ ...form, grabInicio: e.target.value })} data-testid="meeting-grab-inicio-input" />
                  </div>
                  <div>
                    <Label>Fin</Label>
                    <Input type="time" value={form.grabFin} onChange={(e) => setForm({ ...form, grabFin: e.target.value })} data-testid="meeting-grab-fin-input" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="meeting-save-btn">{saving ? 'Guardando…' : (editingId ? 'Guardar cambios' : 'Crear reunión')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
